/** @jsx jsx */
import { customColors } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import React from 'react';
import SplitPane from 'react-split-pane';

import { ResizerStyles } from '../resizerStyles/ResizerStyles';

export type l3MenuSettings = {
  /**
   *  L3 Menu width value
   */
  width: number;

  /**
   *  L3 Menu min width value
   */
  minWidth: number;

  /**
   *  L3 Menu max width value
   */
  maxWidth: number;

  /**
   *  setter for L3 Menu width
   */
  setWidth?: (value: React.SetStateAction<number>) => void;
};

export interface Props {
  /**
   * A title to render, usually rendered with `PageTitle`.
   *
   * As an alternative, you can render your title directly in your children
   */
  title?: React.ReactNode;

  /**
   * Main page content
   */
  children?: React.ReactNode;

  /**
   * Optional right pane
   */
  rightPane?: React.ReactNode;
  /**
   * Optional L3 Menu
   */
  l3Menu?: React.ReactNode;
  /**
   * Optional L3 Menu settings props.
   */

  l3MenuProps?: l3MenuSettings;

  className?: string;
}

/**
 * Page layout component intended to wrap all pages
 */
export const PageLayout = React.forwardRef<HTMLDivElement, Props>(
  ({ title, children, rightPane, l3MenuProps, l3Menu, className }, ref) => (
    <div
      className={classnames(className, 'flex h-full flex-1 flex-col')}
      ref={ref}
    >
      <div>{title}</div>
      {l3Menu && l3MenuProps ? (
        <div className="relative flex-1 overflow-auto">
          <ResizerStyles
            additionalCss={`
      .Resizer:before {
        background-color: ${customColors.bg.secondary};
      }
      .Resizer:hover:before {
        background-color: ${customColors.bg.secondary};
      }
    `}
          />
          <SplitPane
            css={css`
              .Pane2 {
                /* XXX: fixes non-stop jittering when scrolling to a wide row */
                overflow-x: hidden;
              }
            `}
            primary="first"
            minSize={l3MenuProps?.minWidth}
            maxSize={l3MenuProps?.maxWidth}
            size={l3MenuProps?.width}
            onChange={(newWidth) => {
              l3MenuProps?.setWidth?.(newWidth);
            }}
          >
            {l3Menu && <div className="flex h-full flex-col">{l3Menu}</div>}
            <div className="flex h-full flex-1">
              <div
                className="flex flex-2 flex-col overflow-auto"
                data-testid="page-layout"
              >
                {children}
              </div>

              {rightPane && (
                <div className="flex-1 overflow-auto">{rightPane}</div>
              )}
            </div>
          </SplitPane>
        </div>
      ) : (
        <div className="relative flex flex-1 overflow-auto">
          <div
            className="flex flex-2 flex-col overflow-auto"
            data-testid="page-layout"
          >
            {children}
          </div>
          {rightPane && <div className="flex-1 overflow-auto">{rightPane}</div>}
        </div>
      )}
    </div>
  ),
);
